import Dexie from 'dexie';
import { broadcastSessionEvents } from '@/lib/cross-tab-sync';
import { handleBackgroundError } from '@/lib/error-handler';
import { enqueueSessionSync } from '@/lib/realtime-sync/plugins/session';
import { enqueueSessionIndexSync } from '@/lib/realtime-sync/plugins/session-index';
import type {
  EventId,
  GameConfig,
  InviteTokenId,
  NewSessionEvent,
  PlayerId,
  Session,
  SessionEvent,
  SessionId,
  SessionMetadata,
  UserId,
} from '@/schemas';
import {
  createEventId,
  NewSessionEventSchema,
  SessionEventSchema,
  SessionId as SessionIdSchema,
} from '@/schemas';
import { upcastSessionEvent } from '@/schemas/events/upcasters';
import { createSessionEventActivityResolver } from '@/stores/event-activity';
import { replaySessionEvents, replaySessionFromSnapshot } from '@/stores/event-replay';
import { db } from './client';
import {
  createSnapshotIfNeeded,
  deserializeSnapshot,
  getEventsAfterSnapshot,
  getLatestSnapshot,
} from './session-snapshots';

// Backward-compat migration removed; new events only

/**
 * Get all events for a session, ordered by timestamp (then ID for consistency)
 * Uses Dexie's indexed sortBy for efficient sorting
 * Invalid events are filtered out and logged
 */
export const getSessionEvents = async (sessionId: SessionId): Promise<SessionEvent[]> => {
  // Use Dexie's indexed sortBy for efficient sorting by timestamp
  const events = await db.sessionEvents.where('sessionId').equals(sessionId).sortBy('timestamp');

  // Ensure deterministic ordering when timestamps are equal by sorting by ID
  // This is critical for correct event replay when events are added in quick succession
  // and might have the same timestamp
  events.sort((a, b) => {
    const timestampDiff = a.timestamp - b.timestamp;
    if (timestampDiff !== 0) {
      return timestampDiff;
    }
    // If timestamps are equal, sort by ID for consistency
    return a.id.localeCompare(b.id);
  });

  // Filter out invalid events and log them
  const validEvents: SessionEvent[] = [];
  for (const event of events) {
    try {
      validEvents.push(upcastSessionEvent(event));
    } catch (error) {
      // Invalid events are already logged by upcastSessionEvent
      // Continue processing other events
      handleBackgroundError(
        error,
        {
          operation: 'getSessionEvents',
          sessionId,
          eventId: (event as { id?: string })?.id ?? 'unknown',
          eventType: (event as { type?: string })?.type ?? 'unknown',
        },
        'warn',
      );
    }
  }

  return validEvents;
};

/**
 * Add a session event (append-only)
 */
export const addSessionEvent = async (event: NewSessionEvent): Promise<EventId> => {
  const [id] = await addSessionEventsBatch([event]);
  if (!id) {
    throw new Error('Failed to create session event');
  }
  return id;
};

/**
 * Add multiple session events atomically. Either every event is written or none of them are.
 */
export const addSessionEventsBatch = async (events: NewSessionEvent[]): Promise<EventId[]> => {
  if (events.length === 0) {
    return [];
  }

  const validatedEvents: NewSessionEvent[] = events.map((event) =>
    NewSessionEventSchema.parse(event),
  );

  const sessionIds = new Set(validatedEvents.map((event) => event.sessionId));
  if (sessionIds.size !== 1) {
    throw new Error('All events in a batch must target the same session');
  }

  const sessionId = SessionIdSchema.parse(validatedEvents[0].sessionId);

  let lastTimestamp = Date.now();
  const fullEvents: SessionEvent[] = [];

  const performWrite = async () => {
    const collectedIds: EventId[] = [];

    for (const event of validatedEvents) {
      const id = createEventId();
      const now = Date.now();
      const timestamp = now > lastTimestamp ? now : lastTimestamp + 1;
      lastTimestamp = timestamp;

      const fullEvent = SessionEventSchema.parse({
        ...event,
        id,
        timestamp,
      });
      await db.sessionEvents.add(fullEvent);
      fullEvents.push(fullEvent);
      collectedIds.push(id);
    }

    await updateSessionMetadataFromEvents(sessionId, fullEvents);

    return collectedIds;
  };

  const ids = Dexie.currentTransaction
    ? await performWrite()
    : await db.transaction('rw', db.sessionEvents, db.sessionMetadata, performWrite);

  if (fullEvents.length > 0) {
    broadcastSessionEvents(fullEvents);
  }

  enqueueSessionSync(sessionId);
  const sessionStartedEvent = fullEvents.find(
    (event): event is Extract<SessionEvent, { type: 'SessionStarted' }> =>
      event.type === 'SessionStarted',
  );
  if (sessionStartedEvent?.userId) {
    enqueueSessionIndexSync({
      sessionId,
      userId: sessionStartedEvent.userId,
      options: {
        createdAt: sessionStartedEvent.timestamp,
        updatedAt: sessionStartedEvent.timestamp,
      },
    });
  }

  const sessionDeletedEvent = fullEvents.find(
    (event): event is Extract<SessionEvent, { type: 'SessionDeleted' }> =>
      event.type === 'SessionDeleted',
  );
  if (sessionDeletedEvent && sessionDeletedEvent.userId) {
    enqueueSessionIndexSync({
      sessionId,
      userId: sessionDeletedEvent.userId,
      options: {
        updatedAt: sessionDeletedEvent.timestamp,
        deletedAt: sessionDeletedEvent.timestamp,
      },
    });
  }

  // Trigger snapshot creation if needed (non-blocking, background)
  // Check event count after writing events
  const allEvents = await getSessionEvents(sessionId);
  const eventCount = allEvents.length;
  if (eventCount > 0) {
    // Use requestIdleCallback if available, otherwise setTimeout
    if (typeof requestIdleCallback === 'undefined') {
      setTimeout(() => {
        void createSnapshotIfNeeded(sessionId, eventCount);
      }, 0);
    } else {
      requestIdleCallback(() => {
        void createSnapshotIfNeeded(sessionId, eventCount);
      });
    }
  }

  return ids;
};

/**
 * Update session metadata cache from events
 * Called after adding a session event
 */
export const updateSessionMetadataFromEvents = async (
  sessionId: SessionId,
  appendedEvents?: SessionEvent[],
): Promise<void> => {
  try {
    const shouldRebuild =
      !appendedEvents ||
      appendedEvents.length === 0 ||
      appendedEvents.some((event) => event.type === 'EventUndone' || event.type === 'EventRedone');

    if (shouldRebuild) {
      await rebuildSessionMetadata(sessionId);
      return;
    }

    const newEvents = appendedEvents.toSorted((a, b) => a.timestamp - b.timestamp);

    let metadata = await db.sessionMetadata.get(sessionId);

    if (metadata && newEvents.some((event) => event.type === 'SessionStarted')) {
      await rebuildSessionMetadata(sessionId);
      return;
    }

    if (metadata) {
      metadata = { ...metadata };
    } else {
      metadata = createMetadataFromEvents(newEvents);
      if (!metadata) {
        await rebuildSessionMetadata(sessionId);
        return;
      }
    }

    for (const event of newEvents) {
      applyEventToMetadata(metadata, event);
    }

    // Ensure sane values
    metadata.name = metadata.name ?? undefined;
    metadata.playerCount = Math.max(0, metadata.playerCount);

    await db.sessionMetadata.put(metadata);
  } catch (error) {
    // Report error to Sentry and attempt to rebuild metadata as fallback
    handleBackgroundError(error, {
      operation: 'updateSessionMetadataFromEvents',
      sessionId,
      eventCount: appendedEvents?.length ?? 0,
    });
    // Attempt to rebuild metadata from all events as fallback
    try {
      const success = await rebuildSessionMetadata(sessionId);
      if (!success) {
        handleBackgroundError(new Error('Failed to rebuild metadata'), {
          operation: 'rebuildSessionMetadataFallback',
          sessionId,
        });
      }
    } catch (rebuildError) {
      // If rebuild also fails, log but don't throw (to avoid breaking event writes)
      handleBackgroundError(rebuildError, {
        operation: 'rebuildSessionMetadataFallback',
        sessionId,
      });
    }
  }
};

const createMetadataFromEvents = (events: SessionEvent[]): SessionMetadata | undefined => {
  const sessionStarted = events.find((event) => event.type === 'SessionStarted');

  if (!sessionStarted || sessionStarted.type !== 'SessionStarted') {
    return undefined;
  }

  const metadata: SessionMetadata = {
    id: sessionStarted.sessionId,
    name: sessionStarted.payload.name,
    playerCount: 0,
    status: 'ongoing',
    startedAt: sessionStarted.timestamp,
    endedAt: undefined,
    pausedAt: undefined,
    deletedAt: undefined,
    archivedAt: undefined,
    createdAt: sessionStarted.timestamp,
    updatedAt: sessionStarted.timestamp,
    currentRoundNumber: 0,
    winner: undefined,
    userId: sessionStarted.userId, // Extract userId from SessionStarted event
    inviteToken: undefined,
    isPublicSpectator: false,
    isPublicPlayerJoin: false,
  };

  return metadata;
};

/**
 * Extract deletedAt timestamp from events
 */
function extractDeletedAt(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): number | undefined {
  for (const event of events) {
    if (event.type === 'SessionDeleted' && activityResolver.isActive(event.id)) {
      return event.timestamp;
    }
  }
  return undefined;
}

/**
 * Extract userId from SessionStarted and SessionOwnershipClaimed events
 */
function extractUserId(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): UserId | undefined {
  const sessionStarted = events.find((event) => event.type === 'SessionStarted');
  const ownershipClaim = events
    .toReversed()
    .find(
      (event) => event.type === 'SessionOwnershipClaimed' && activityResolver.isActive(event.id),
    );
  return ownershipClaim?.userId ?? sessionStarted?.userId;
}

/**
 * Process invite token event
 */
function processInviteTokenEvent(
  event: SessionEvent,
  currentToken: InviteTokenId | undefined,
): InviteTokenId | undefined {
  if (event.type === 'InviteTokenGenerated' && currentToken === undefined) {
    return event.payload.inviteToken;
  }
  if (event.type === 'InviteTokenRevoked' && currentToken !== undefined) {
    return undefined;
  }
  return currentToken;
}

/**
 * Process visibility event
 */
function processVisibilityEvent(
  event: SessionEvent,
  currentSpectator: boolean,
  currentPlayerJoin: boolean,
): { isPublicSpectator: boolean; isPublicPlayerJoin: boolean } {
  if (event.type === 'SessionPublicSpectatorEnabled' && !currentSpectator) {
    return { isPublicSpectator: true, isPublicPlayerJoin: currentPlayerJoin };
  }
  if (event.type === 'SessionPublicSpectatorDisabled' && currentSpectator) {
    return { isPublicSpectator: false, isPublicPlayerJoin: currentPlayerJoin };
  }
  if (event.type === 'SessionPublicPlayerJoinEnabled' && !currentPlayerJoin) {
    return { isPublicSpectator: currentSpectator, isPublicPlayerJoin: true };
  }
  if (event.type === 'SessionPublicPlayerJoinDisabled' && currentPlayerJoin) {
    return { isPublicSpectator: currentSpectator, isPublicPlayerJoin: false };
  }
  return { isPublicSpectator: currentSpectator, isPublicPlayerJoin: currentPlayerJoin };
}

/**
 * Extract invite token and visibility settings from events
 */
function extractInviteTokenAndVisibility(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): {
  inviteToken: InviteTokenId | undefined;
  isPublicSpectator: boolean;
  isPublicPlayerJoin: boolean;
} {
  let inviteToken: InviteTokenId | undefined;
  let isPublicSpectator = false;
  let isPublicPlayerJoin = false;

  for (const event of events.toReversed()) {
    if (!activityResolver.isActive(event.id)) continue;

    const newToken = processInviteTokenEvent(event, inviteToken);
    if (newToken !== inviteToken) {
      inviteToken = newToken;
      continue;
    }

    const visibilityState = processVisibilityEvent(event, isPublicSpectator, isPublicPlayerJoin);
    if (
      visibilityState.isPublicSpectator !== isPublicSpectator ||
      visibilityState.isPublicPlayerJoin !== isPublicPlayerJoin
    ) {
      isPublicSpectator = visibilityState.isPublicSpectator;
      isPublicPlayerJoin = visibilityState.isPublicPlayerJoin;
    }
  }

  return { inviteToken, isPublicSpectator, isPublicPlayerJoin };
}

const applyEventToMetadata = (metadata: SessionMetadata, event: SessionEvent) => {
  switch (event.type) {
    case 'SessionStarted': {
      metadata.name = event.payload.name;
      metadata.startedAt = event.timestamp;
      metadata.createdAt = event.timestamp;
      metadata.status = 'ongoing';
      metadata.deletedAt = undefined;
      metadata.endedAt = undefined;
      metadata.pausedAt = undefined;
      metadata.archivedAt = undefined;
      metadata.currentRoundNumber = 0;
      metadata.playerCount = 0;
      break;
    }
    case 'SessionNameChanged': {
      metadata.name = event.payload.name;
      break;
    }
    case 'SessionPaused': {
      metadata.status = 'paused';
      metadata.pausedAt = event.timestamp;
      break;
    }
    case 'SessionResumed': {
      metadata.status = 'ongoing';
      metadata.pausedAt = undefined;
      break;
    }
    case 'SessionRestarted': {
      metadata.status = 'ongoing';
      metadata.pausedAt = undefined;
      metadata.endedAt = undefined;
      metadata.currentRoundNumber = 0;
      break;
    }
    case 'SessionEnded': {
      metadata.status = 'finished';
      metadata.endedAt = event.timestamp;
      metadata.pausedAt = undefined;
      // Winner will be set by SessionWinnerSet event or calculated during rebuild
      break;
    }
    case 'SessionArchived': {
      const archivedAt = event.payload.archivedAt ?? event.timestamp;
      metadata.archivedAt = archivedAt;
      break;
    }
    case 'SessionUnarchived': {
      metadata.archivedAt = undefined;
      break;
    }
    case 'SessionWinnerSet': {
      metadata.winner =
        event.payload.winnerIds.length === 1 ? event.payload.winnerIds[0] : undefined;
      break;
    }
    case 'SessionOwnershipClaimed': {
      metadata.userId = event.userId ?? metadata.userId;
      break;
    }
    case 'SessionDeleted': {
      metadata.deletedAt = event.timestamp;
      break;
    }
    case 'RoundStarted': {
      metadata.currentRoundNumber = Math.max(metadata.currentRoundNumber, event.payload.number);
      break;
    }
    case 'PlayerJoined': {
      metadata.playerCount += 1;
      break;
    }
    case 'PlayerLeft':
    case 'PlayerRemoved': {
      metadata.playerCount = Math.max(0, metadata.playerCount - 1);
      break;
    }
    case 'InviteTokenGenerated': {
      metadata.inviteToken = event.payload.inviteToken;
      break;
    }
    case 'InviteTokenRevoked': {
      metadata.inviteToken = undefined;
      break;
    }
    case 'SessionPublicSpectatorEnabled': {
      metadata.isPublicSpectator = true;
      break;
    }
    case 'SessionPublicSpectatorDisabled': {
      metadata.isPublicSpectator = false;
      break;
    }
    case 'SessionPublicPlayerJoinEnabled': {
      metadata.isPublicPlayerJoin = true;
      break;
    }
    case 'SessionPublicPlayerJoinDisabled': {
      metadata.isPublicPlayerJoin = false;
      break;
    }
    case 'SessionNotesChanged':
    case 'SessionGameConfigChanged':
    case 'SessionTagAdded':
    case 'SessionTagRemoved':
    case 'InviteLinkEnabled':
    case 'InviteLinkDisabled':
    case 'JoinRequestCreated':
    case 'JoinRequestApproved':
    case 'JoinRequestRejected':
    case 'RoundCompleted':
    case 'RoundDeleted':
    case 'RoundNoteAdded':
    case 'RoundNoteRemoved':
    case 'RoundTagAdded':
    case 'RoundTagRemoved':
    case 'ScoreChanged':
    case 'ScoreSet':
    case 'PlayerOrderChanged':
    case 'PlayerIdMapped':
    case 'PlayerIdUnmapped':
    case 'SpectatorJoined':
    case 'SpectatorLeft':
    case 'ImageAdded':
    case 'ImageRemoved':
    case 'ImageCaptionChanged':
    case 'ImagePrimarySet': {
      // Image events don't affect session metadata (only affect images table and session state)
      break;
    }
    case 'EventUndone':
    case 'EventRedone': {
      // Should never be handled incrementally
      break;
    }
    default: {
      // TypeScript narrows to 'never' here, but we need runtime safety
      // for potential future event types. Use type assertion to access properties.
      const unhandledEvent = event as SessionEvent;
      handleBackgroundError(
        new Error(`Unhandled event type: ${unhandledEvent.type}`),
        { operation: 'updateMetadataIncremental', eventType: unhandledEvent.type },
        'warn',
      );
    }
  }

  metadata.updatedAt = event.timestamp;
};

/**
 * Create a synthetic SessionStarted event from existing events
 * Used to repair sessions that are missing the SessionStarted event
 *
 * @param sessionId - Session ID
 * @param events - Existing events for the session
 * @param activityResolver - Event activity resolver to filter active events
 * @returns Synthetic SessionStarted event or undefined if insufficient data
 */
/**
 * Extract session name from SessionNameChanged events
 */
function extractSessionName(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): string | undefined {
  for (const event of events.toReversed()) {
    if (
      event.type === 'SessionNameChanged' &&
      activityResolver.isActive(event.id) &&
      event.payload.name
    ) {
      return event.payload.name;
    }
  }
  return undefined;
}

/**
 * Extract session notes from SessionNotesChanged events
 */
function extractSessionNotes(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): string | undefined {
  for (const event of events.toReversed()) {
    if (event.type === 'SessionNotesChanged' && activityResolver.isActive(event.id)) {
      return event.payload.notes;
    }
  }
  return undefined;
}

/**
 * Extract active player IDs from PlayerJoined/PlayerLeft/PlayerRemoved events
 */
function extractActivePlayerIds(
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): Set<string> {
  const playerIds = new Set<string>();
  for (const event of events) {
    if (event.type === 'PlayerJoined' && activityResolver.isActive(event.id)) {
      playerIds.add(event.payload.playerId);
    } else if (
      (event.type === 'PlayerLeft' || event.type === 'PlayerRemoved') &&
      activityResolver.isActive(event.id)
    ) {
      playerIds.delete(event.payload.playerId);
    }
  }
  return playerIds;
}

function createSyntheticSessionStarted(
  sessionId: SessionId,
  events: SessionEvent[],
  activityResolver: ReturnType<typeof createSessionEventActivityResolver>,
): SessionEvent | undefined {
  // Find the earliest event timestamp
  let earliestEvent = events[0];
  if (!earliestEvent) {
    return undefined;
  }
  for (const event of events) {
    if (event.timestamp < earliestEvent.timestamp) {
      earliestEvent = event;
    }
  }

  // Extract userId from events
  const userId = extractUserId(events, activityResolver);

  // Extract name, notes, and playerIds
  const sessionName = extractSessionName(events, activityResolver);
  const sessionNotes = extractSessionNotes(events, activityResolver);
  const playerIds = extractActivePlayerIds(events, activityResolver);

  // Need at least one player to create a valid SessionStarted event
  if (playerIds.size === 0) {
    return undefined;
  }

  // Use default game config (can't infer from events without SessionStarted)
  const defaultGameConfig: GameConfig = {
    hasRounds: false,
    winCondition: 'mostPoints',
    roundTargetPoints: undefined,
    gameTargetPoints: undefined,
    targetRounds: undefined,
    teamScoringType: undefined,
    version: 1,
  };

  // Create synthetic SessionStarted event
  const syntheticEvent: SessionEvent = {
    id: createEventId(),
    sessionId,
    timestamp: earliestEvent.timestamp - 1, // Place it before the earliest event
    userId,
    type: 'SessionStarted',
    payload: {
      name: sessionName,
      notes: sessionNotes,
      gameConfigSnapshot: defaultGameConfig,
      playerIds: [...playerIds] as PlayerId[],
      teamConfiguration: undefined, // Can't infer team configuration from events alone
    },
  };

  return syntheticEvent;
}

const rebuildSessionMetadata = async (sessionId: SessionId): Promise<boolean> => {
  const events = await getSessionEvents(sessionId);

  if (events.length === 0) return false;

  const activityResolver = createSessionEventActivityResolver(events);
  const deletedAt = extractDeletedAt(events, activityResolver);

  let session: Session;
  try {
    session = replaySessionEvents(events);
  } catch (error: unknown) {
    // If replay fails due to missing SessionStarted, try to repair it
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('SessionStarted event not found')) {
      // Try to create a synthetic SessionStarted event
      const syntheticStarted = createSyntheticSessionStarted(sessionId, events, activityResolver);

      if (syntheticStarted) {
        // Prepend synthetic event and try replay again
        const eventsWithSynthetic = [syntheticStarted, ...events];
        try {
          session = replaySessionEvents(eventsWithSynthetic);
          // Log successful repair (don't log the original error since we fixed it)
          console.log(
            `[Data Integrity] Successfully repaired session ${sessionId} by creating synthetic SessionStarted event`,
          );
          // Continue to build metadata (don't return false)
        } catch (replayError) {
          // Still failed even with synthetic event
          handleBackgroundError(replayError, {
            operation: 'rebuildSessionMetadata',
            sessionId,
            repairAttempted: true,
          });
          return false;
        }
      } else {
        // Can't create synthetic event (insufficient data - likely no players)
        // Try to use existing metadata to create a better repair
        const existingMetadata = await db.sessionMetadata.get(sessionId);

        // Collect diagnostic information to help understand why synthetic event creation failed
        const eventTypes = [...new Set(events.map((e) => e.type))];
        const playerJoinedCount = events.filter(
          (e) => e.type === 'PlayerJoined' && activityResolver.isActive(e.id),
        ).length;
        const playerLeftCount = events.filter(
          (e) =>
            (e.type === 'PlayerLeft' || e.type === 'PlayerRemoved') &&
            activityResolver.isActive(e.id),
        ).length;
        const hasUserId = extractUserId(events, activityResolver) !== undefined;
        const hasActivePlayers = playerJoinedCount > playerLeftCount;

        // Log as warning since we will successfully repair metadata using fallback method
        handleBackgroundError(
          new Error(
            `Cannot create synthetic SessionStarted: insufficient data (no active players). Metadata will be repaired using fallback method.`,
          ),
          {
            operation: 'rebuildSessionMetadata',
            sessionId,
            repairAttempted: true,
            repairMethod: 'fallback',
            originalError: errorMessage,
            diagnostic: {
              totalEvents: events.length,
              eventTypes,
              playerJoinedCount,
              playerLeftCount,
              hasActivePlayers,
              hasUserId,
              earliestTimestamp: events[0]?.timestamp,
              latestTimestamp: events.at(-1)?.timestamp,
              hasExistingMetadata: existingMetadata !== undefined,
              reason:
                'No active players found - cannot create valid SessionStarted event (requires min 1 player)',
            },
          },
          'warn',
        );

        // Try to repair metadata using existing metadata as a base
        // This preserves as much information as possible even if we can't replay
        const repairedMetadata: SessionMetadata = {
          id: sessionId,
          name: existingMetadata?.name,
          playerCount: 0, // No active players
          status: existingMetadata?.status ?? ('finished' as const),
          startedAt: existingMetadata?.startedAt ?? events[0]?.timestamp ?? Date.now(),
          endedAt: existingMetadata?.endedAt ?? events.at(-1)?.timestamp,
          pausedAt: existingMetadata?.pausedAt,
          deletedAt: existingMetadata?.deletedAt ?? deletedAt,
          archivedAt: existingMetadata?.archivedAt,
          createdAt: existingMetadata?.createdAt ?? events[0]?.timestamp ?? Date.now(),
          updatedAt: events.at(-1)?.timestamp ?? Date.now(),
          currentRoundNumber: existingMetadata?.currentRoundNumber ?? 0,
          winner: existingMetadata?.winner,
          userId: existingMetadata?.userId ?? extractUserId(events, activityResolver),
          gameDefinitionId: existingMetadata?.gameDefinitionId,
          inviteToken: existingMetadata?.inviteToken,
          isPublicSpectator: existingMetadata?.isPublicSpectator ?? false,
          isPublicPlayerJoin: existingMetadata?.isPublicPlayerJoin ?? false,
        };

        await db.sessionMetadata.put(repairedMetadata);
        // Return true since we did repair the metadata (even if not perfectly)
        return true;
      }
    } else {
      // Different error, handle normally
      handleBackgroundError(error, { operation: 'rebuildSessionMetadata', sessionId });
      return false;
    }
  }

  const lastEventTimestamp = events.at(-1)?.timestamp ?? session.updatedAt;
  const userId = extractUserId(events, activityResolver);

  // Preserve existing gameDefinitionId if it exists
  const existingMetadata = await db.sessionMetadata.get(sessionId);
  const gameDefinitionId = existingMetadata?.gameDefinitionId;

  // Extract invite token and visibility settings from events
  const { inviteToken, isPublicSpectator, isPublicPlayerJoin } = extractInviteTokenAndVisibility(
    events,
    activityResolver,
  );

  const metadata: SessionMetadata = {
    id: sessionId,
    name: session.name,
    playerCount: session.players.filter((player) => !player.deletedAt).length,
    status: session.status,
    startedAt: session.startedAt,
    endedAt: session.endedAt,
    pausedAt: session.pausedAt,
    deletedAt,
    archivedAt: session.archivedAt,
    createdAt: session.createdAt,
    updatedAt: lastEventTimestamp,
    currentRoundNumber: session.currentRoundNumber,
    winner: session.winner,
    userId, // Include userId from SessionStarted event
    gameDefinitionId, // Preserve existing gameDefinitionId if it exists
    inviteToken: inviteToken ?? undefined,
    isPublicSpectator,
    isPublicPlayerJoin,
  };

  await db.sessionMetadata.put(metadata);
  return true;
};

/**
 * Load and replay session events to rebuild full Session object
 * Uses snapshot if available for faster replay of large sessions
 *
 * @remarks
 * This function is snapshot-aware:
 * 1. Tries to load latest snapshot
 * 2. If snapshot exists and is valid:
 *    - Deserializes snapshot to get Session state
 *    - Loads events after snapshot's eventCount
 *    - Replays delta events on top of snapshot
 * 3. If no snapshot or snapshot invalid:
 *    - Falls back to full replay (existing behavior)
 *
 * @param sessionId - Session ID to replay
 * @returns Complete Session object reconstructed from events or snapshot + delta
 */
/**
 * Attempts to replay session from snapshot with delta events
 * Falls back to full replay if snapshot is invalid or player mapping events are present
 */
async function replayFromSnapshot(
  sessionId: SessionId,
  snapshot: NonNullable<Awaited<ReturnType<typeof getLatestSnapshot>>>,
): Promise<Session | undefined> {
  try {
    // Deserialize snapshot to get Session state
    const snapshotSession = await deserializeSnapshot(snapshot);

    // Get events after the snapshot
    const deltaEvents = await getEventsAfterSnapshot(sessionId, snapshot.eventCount);

    // If there are no delta events, return snapshot as-is
    if (deltaEvents.length === 0) {
      return snapshotSession;
    }

    // PlayerIdMapped/PlayerIdUnmapped events require full replay context
    // If they appear in delta, fall back to full replay for correctness
    const hasPlayerMappingEvents = deltaEvents.some(
      (e) => e.type === 'PlayerIdMapped' || e.type === 'PlayerIdUnmapped',
    );
    if (hasPlayerMappingEvents) {
      // Fall back to full replay for player mapping events
      const events = await getSessionEvents(sessionId);
      return replaySessionEvents(events);
    }

    // Replay delta events on top of snapshot
    return replaySessionFromSnapshot(snapshotSession, deltaEvents);
  } catch (error) {
    // If snapshot is corrupted or invalid, fall back to full replay
    handleBackgroundError(error, { operation: 'replayFromSnapshot', sessionId }, 'warn');
    return undefined;
  }
}

/**
 * Attempts to replay session from events, with repair logic for missing SessionStarted
 */
function replayWithRepair(sessionId: SessionId, events: SessionEvent[]): Session {
  try {
    return replaySessionEvents(events);
  } catch (error: unknown) {
    // If replay fails due to missing SessionStarted, try to repair it
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('SessionStarted event not found')) {
      const activityResolver = createSessionEventActivityResolver(events);
      const syntheticStarted = createSyntheticSessionStarted(sessionId, events, activityResolver);

      if (syntheticStarted) {
        // Prepend synthetic event and try replay again
        const eventsWithSynthetic = [syntheticStarted, ...events];
        return replaySessionEvents(eventsWithSynthetic);
      }
      // Can't create synthetic event - throw the original error
      // The caller should handle this gracefully
    }
    // Re-throw other errors
    throw error;
  }
}

export const replaySessionFromEvents = async (sessionId: SessionId): Promise<Session> => {
  // Try to load latest snapshot
  const snapshot = await getLatestSnapshot(sessionId);

  if (snapshot) {
    const result = await replayFromSnapshot(sessionId, snapshot);
    if (result !== undefined) {
      return result;
    }
  }

  // Fallback to full replay (existing behavior)
  const events = await getSessionEvents(sessionId);
  return replayWithRepair(sessionId, events);
};

/**
 * Validation result for session metadata
 */
export interface SessionMetadataValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate that session metadata matches the replayed session from events
 *
 * This function helps detect data corruption or inconsistencies between
 * the denormalized metadata cache and the source of truth (events).
 *
 * @param sessionId - The session ID to validate
 * @returns Validation result with errors if any mismatches are found
 */
export async function validateSessionMetadata(
  sessionId: SessionId,
): Promise<SessionMetadataValidationResult> {
  const metadata = await db.sessionMetadata.get(sessionId);

  // If no metadata exists, that's an error
  if (!metadata) {
    return {
      isValid: false,
      errors: [`Metadata not found for session ${sessionId}`],
    };
  }

  // Replay events to get the actual session state
  let session: Session;
  let events: SessionEvent[];
  try {
    events = await getSessionEvents(sessionId);
    if (events.length === 0) {
      return {
        isValid: false,
        errors: [`No events found for session ${sessionId}`],
      };
    }
    session = replaySessionEvents(events);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      isValid: false,
      errors: [`Failed to replay events for session ${sessionId}: ${errorMessage}`],
    };
  }

  const errors = computeSessionMetadataErrors({ metadata, session, events });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function computeSessionMetadataErrors({
  metadata,
  session,
  events,
}: {
  metadata: SessionMetadata;
  session: Session;
  events: SessionEvent[];
}): string[] {
  const activityResolver = createSessionEventActivityResolver(events);
  let deletedAt: number | undefined;
  for (const event of events) {
    if (event.type === 'SessionDeleted' && activityResolver.isActive(event.id)) {
      deletedAt = event.timestamp;
    }
  }

  const lastEventTimestamp = events.at(-1)?.timestamp ?? session.updatedAt;
  const expectedPlayerCount = session.players.filter((player) => !player.deletedAt).length;
  const metadataDeletedAt = metadata.deletedAt ?? undefined;
  const expectedArchivedAt = session.archivedAt ?? undefined;
  const metadataArchivedAt = metadata.archivedAt ?? undefined;
  const comparisons: Array<[boolean, string]> = [
    [
      metadata.name !== session.name,
      `Name mismatch: metadata="${metadata.name ?? 'undefined'}", session="${session.name ?? 'undefined'}"`,
    ],
    [
      metadata.playerCount !== expectedPlayerCount,
      `Player count mismatch: metadata=${metadata.playerCount}, session=${expectedPlayerCount}`,
    ],
    [
      metadata.status !== session.status,
      `Status mismatch: metadata="${metadata.status}", session="${session.status}"`,
    ],
    [
      metadata.createdAt !== session.createdAt,
      `CreatedAt mismatch: metadata=${metadata.createdAt}, session=${session.createdAt}`,
    ],
    [
      metadata.updatedAt !== lastEventTimestamp,
      `UpdatedAt mismatch: metadata=${metadata.updatedAt}, session=${lastEventTimestamp}`,
    ],
    [
      metadata.startedAt !== session.startedAt,
      `StartedAt mismatch: metadata=${metadata.startedAt}, session=${session.startedAt}`,
    ],
    [
      metadata.currentRoundNumber !== session.currentRoundNumber,
      `CurrentRoundNumber mismatch: metadata=${metadata.currentRoundNumber}, session=${session.currentRoundNumber}`,
    ],
    [
      metadata.winner !== session.winner,
      `Winner mismatch: metadata="${metadata.winner ?? 'undefined'}", session="${session.winner ?? 'undefined'}"`,
    ],
    [
      metadataDeletedAt !== (deletedAt ?? undefined),
      `Deleted at mismatch: metadata=${metadata.deletedAt ?? 'undefined'}, session=${deletedAt ?? 'undefined'}`,
    ],
    [
      metadataArchivedAt !== expectedArchivedAt,
      `ArchivedAt mismatch: metadata=${metadata.archivedAt ?? 'undefined'}, session=${expectedArchivedAt ?? 'undefined'}`,
    ],
  ];

  const sessionStarted = events.find((event) => event.type === 'SessionStarted');
  const expectedUserId = sessionStarted?.userId;
  comparisons.push([
    metadata.userId !== expectedUserId,
    `UserId mismatch: metadata="${metadata.userId ?? 'undefined'}", session="${expectedUserId ?? 'undefined'}"`,
  ]);

  if (session.status === 'finished') {
    comparisons.push([
      metadata.endedAt !== session.endedAt,
      `EndedAt mismatch: metadata=${metadata.endedAt ?? 'undefined'}, session=${session.endedAt ?? 'undefined'}`,
    ]);
  }

  if (session.status === 'paused') {
    comparisons.push([
      metadata.pausedAt !== session.pausedAt,
      `PausedAt mismatch: metadata=${metadata.pausedAt ?? 'undefined'}, session=${session.pausedAt ?? 'undefined'}`,
    ]);
  }

  const errors: string[] = [];
  for (const [condition, message] of comparisons) {
    if (condition) {
      errors.push(message);
    }
  }

  return errors;
}

/**
 * Check for data integrity issues: sessions with events but missing metadata
 * Reports to Sentry if inconsistencies are found
 *
 * @returns Statistics about detected issues
 */
export interface DataIntegrityCheckResult {
  readonly totalSessionsWithEvents: number;
  readonly sessionsWithMissingMetadata: number;
  readonly sessionsWithCorruptedMetadata: number;
  readonly issues: Array<{ sessionId: SessionId; issue: string }>;
}

export async function checkDataIntegrity(): Promise<DataIntegrityCheckResult> {
  const result = {
    totalSessionsWithEvents: 0,
    sessionsWithMissingMetadata: 0,
    sessionsWithCorruptedMetadata: 0,
    issues: [] as Array<{ sessionId: SessionId; issue: string }>,
  };

  try {
    // Get all unique session IDs from events
    const allEvents = await db.sessionEvents.toArray();
    const sessionIdsSet = new Set<SessionId>(allEvents.map((event) => event.sessionId));
    const sessionIds = [...sessionIdsSet];

    result.totalSessionsWithEvents = sessionIds.length;

    // Check each session for integrity issues
    for (const sessionId of sessionIds) {
      try {
        const metadata = await db.sessionMetadata.get(sessionId);
        const events = await getSessionEvents(sessionId);

        if (events.length === 0) {
          // No events, skip
          continue;
        }

        // Check if metadata is missing
        if (!metadata) {
          result.sessionsWithMissingMetadata += 1;
          result.issues.push({
            sessionId,
            issue: 'Metadata missing for session with events',
          });
          continue;
        }

        // Validate metadata matches events
        const validation = await validateSessionMetadata(sessionId);
        if (!validation.isValid) {
          result.sessionsWithCorruptedMetadata += 1;
          result.issues.push({
            sessionId,
            issue: `Metadata corruption: ${validation.errors.join('; ')}`,
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        result.issues.push({
          sessionId,
          issue: `Error checking integrity: ${errorMessage}`,
        });
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    result.issues.push({
      sessionId: '' as SessionId,
      issue: `Failed to check integrity: ${errorMessage}`,
    });
  }

  return result;
}

/**
 * Repair session metadata by rebuilding it from events for all sessions
 * that have events but missing or corrupted metadata
 *
 * @returns Statistics about the repair operation
 */
export interface RepairSessionMetadataResult {
  readonly totalSessionsWithEvents: number;
  readonly sessionsRepaired: number;
  readonly sessionsSkipped: number;
  readonly errors: Array<{ sessionId: SessionId; error: string }>;
}

/**
 * Check if a session needs metadata repair
 */
async function needsMetadataRepair(sessionId: SessionId): Promise<boolean> {
  const metadata = await db.sessionMetadata.get(sessionId);
  if (!metadata) {
    return true;
  }

  const validation = await validateSessionMetadata(sessionId);
  return !validation.isValid;
}

/**
 * Repair metadata for a single session
 */
async function repairSingleSessionMetadata(
  sessionId: SessionId,
): Promise<{ repaired: boolean; skipped: boolean }> {
  const events = await getSessionEvents(sessionId);

  if (events.length === 0) {
    return { repaired: false, skipped: true };
  }

  const needsRepair = await needsMetadataRepair(sessionId);
  if (!needsRepair) {
    return { repaired: false, skipped: true };
  }

  const success = await rebuildSessionMetadata(sessionId);
  return { repaired: success, skipped: !success };
}

export async function repairSessionMetadata(): Promise<RepairSessionMetadataResult> {
  const result = {
    totalSessionsWithEvents: 0,
    sessionsRepaired: 0,
    sessionsSkipped: 0,
    errors: [] as Array<{ sessionId: SessionId; error: string }>,
  };

  try {
    // Get all unique session IDs from events
    const allEvents = await db.sessionEvents.toArray();
    const sessionIdsSet = new Set<SessionId>(allEvents.map((event) => event.sessionId));
    const sessionIds = [...sessionIdsSet];

    result.totalSessionsWithEvents = sessionIds.length;

    // For each session, check if metadata exists and is valid
    for (const sessionId of sessionIds) {
      try {
        const repairResult = await repairSingleSessionMetadata(sessionId);

        if (repairResult.repaired) {
          result.sessionsRepaired += 1;
        } else if (repairResult.skipped) {
          result.sessionsSkipped += 1;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        result.errors.push({ sessionId, error: errorMessage });
        console.error(`Failed to repair metadata for session ${sessionId}:`, error);
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Failed to repair session metadata:', error);
    result.errors.push({
      sessionId: '' as SessionId,
      error: `Failed to start repair: ${errorMessage}`,
    });
  }

  return result;
}
